import { useRef, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { MICRO_MARKETS } from "./data";
import { EASE, Eyebrow, MaskLines, Reveal } from "./ui-bits";

const BengaluruMap = () => {
  const ref = useRef(null);
  const [selected, setSelected] = useState(MICRO_MARKETS[2]);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const x = useTransform(scrollYProgress, [0, 1], ["8%", "-12%"]);

  return (
    <section
      ref={ref}
      id="location"
      className="relative overflow-hidden bg-ivory text-obsidian"
      data-testid="location-section"
    >
      <motion.div
        aria-hidden
        style={{ x }}
        className="text-outline-obsidian pointer-events-none select-none whitespace-nowrap pt-10 font-serif text-[22vw] leading-none"
      >
        LOCATION
      </motion.div>

      <div className="mx-auto grid max-w-[1440px] gap-12 px-5 pb-24 md:grid-cols-12 md:px-10 md:pb-36">
        <div className="md:col-span-5">
          <Eyebrow tone="dark">03 / Bengaluru</Eyebrow>
          <h2 className="mt-6 font-serif text-4xl leading-[1.02] sm:text-5xl lg:text-6xl">
            <MaskLines lines={["LOCATION", "IS STRATEGY."]} />
          </h2>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-md text-sm leading-relaxed text-obsidian/70 md:text-base">
              The right address can influence visibility, footfall, talent
              access, customer behaviour and ultimately the economics of a
              business.
            </p>
          </Reveal>

          <div className="mt-10 min-h-[120px]">
            <AnimatePresence mode="wait">
              {selected && (
                <motion.div
                  key={selected.name}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.45, ease: EASE }}
                  className="border-t border-obsidian/15 pt-6"
                  data-testid="map-location-info"
                >
                  <div className="font-serif text-3xl">{selected.name}</div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {selected.tags.map((t) => (
                      <span
                        key={t}
                        className="border border-obsidian/20 px-3 py-1 text-[10px] font-medium uppercase tracking-[0.2em] text-obsidian/70"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="md:col-span-7">
          <Reveal>
            <div className="relative mx-auto aspect-square max-w-[640px]" data-testid="bengaluru-map">
              <svg viewBox="0 0 800 800" className="h-full w-full">
                <g stroke="rgba(11,11,10,0.06)" strokeWidth="1">
                  {[100, 220, 340, 460, 580, 700].map((v) => (
                    <line key={`h${v}`} x1="0" y1={v} x2="800" y2={v} />
                  ))}
                  {[100, 220, 340, 460, 580, 700].map((v) => (
                    <line key={`v${v}`} x1={v} y1="0" x2={v} y2="800" />
                  ))}
                </g>
                <path
                  d="M215,185 L335,120 L470,140 L585,215 L660,325 L640,455 L560,565 L430,645 L300,600 L205,500 L165,370 Z"
                  fill="rgba(11,11,10,0.035)"
                  stroke="rgba(11,11,10,0.35)"
                  strokeWidth="1.5"
                />
                <ellipse
                  cx="430"
                  cy="385"
                  rx="235"
                  ry="215"
                  fill="none"
                  stroke="rgba(183,162,122,0.45)"
                  strokeWidth="1"
                  strokeDasharray="5 7"
                />
                <ellipse
                  cx="420"
                  cy="390"
                  rx="120"
                  ry="110"
                  fill="none"
                  stroke="rgba(11,11,10,0.15)"
                  strokeWidth="1"
                  strokeDasharray="3 6"
                />
              </svg>
              {MICRO_MARKETS.map((m) => (
                <button
                  key={m.name}
                  type="button"
                  onClick={() => setSelected(m)}
                  className="group absolute -translate-x-1/2 -translate-y-1/2"
                  style={{ left: `${(m.x / 800) * 100}%`, top: `${(m.y / 800) * 100}%` }}
                  data-testid={`map-point-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
                  aria-label={m.name}
                >
                  <span
                    className={`map-pulse relative block h-3 w-3 rounded-full transition-colors duration-300 ${
                      selected?.name === m.name ? "bg-champagne" : "bg-obsidian/70 group-hover:bg-champagne"
                    }`}
                  />
                  <span
                    className={`absolute left-4 top-1/2 -translate-y-1/2 whitespace-nowrap text-[10px] font-medium uppercase tracking-[0.18em] transition-opacity duration-300 ${
                      selected?.name === m.name
                        ? "text-obsidian opacity-100"
                        : "text-obsidian/50 opacity-0 group-hover:opacity-100 md:opacity-60"
                    }`}
                  >
                    {m.name}
                  </span>
                </button>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default BengaluruMap;
